import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, LayoutGrid, List, AlertTriangle, Clock, X, SlidersHorizontal } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, EmptyState, Input, Textarea, Select, PaginationBar, SearchInput, SlidePanel, MultiSelect } from '../../../components/ui'
import { formatRelativeDate } from '../../../utils/dateUtils'
import { inputClass } from '../../../styles/forms'
import VoterSearchSelect from '../../../components/VoterSearchSelect'
import { useAuth } from '../../../contexts/AuthContext'

interface Pipeline { id: number; name: string; color: string | null; stages: any[]; demands_count: number }
interface Stage { id: number; name: string; color: string; max_days: number | null; demands?: DemandCard[] }
interface DemandCard { id: number; title: string; voter_name: string | null; category_name: string | null; priority_code: string; priority_label: string; priority_color: string; stage_entered_at: string; days_in_stage: number | null; is_overdue: boolean }
interface DemandListItem { id: number; title: string; voter_name: string | null; voter_phone: string | null; category_name: string | null; leader_name: string | null; creator_name: string | null; priority_code: string; priority_label: string; priority_color: string; status_code: string; status_label: string; status_color: string; stage_name: string; stage_color: string; pipeline_name: string; due_date: string | null; days_in_stage: number | null; is_overdue: boolean; created_at: string; assignees: { id: number; name: string; role: string }[] }
interface Category { id: number; name: string; pipeline_id: number; fields: any[]; children: Category[] }

export default function DemandasPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [stages, setStages] = useState<Stage[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [leaders, setLeaders] = useState<{ id: number; name: string; nickname: string | null }[]>([])
  const [leadersLoaded, setLeadersLoaded] = useState(false)
  const [orgUsers, setOrgUsers] = useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [listLoading, setListLoading] = useState(false)
  const [listDemands, setListDemands] = useState<DemandListItem[]>([])
  const [listMeta, setListMeta] = useState({ total: 0, page: 1, per_page: 10, last_page: 1 })
  const [listPage, setListPage] = useState(1)
  const [listPerPage, setListPerPage] = useState(10)
  const [listSearch, setListSearch] = useState('')
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [listFilters, setListFilters] = useState({ status: '', priority: '', pipeline_id: '', overdue: '' })
  const [tempFilters, setTempFilters] = useState(listFilters)
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board')
  const [selectedPipeline, setSelectedPipeline] = useState<number | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [leaderSearch, setLeaderSearch] = useState('')
  const [leaderDropdownOpen, setLeaderDropdownOpen] = useState(false)
  const [selectedCategoryFields, setSelectedCategoryFields] = useState<any[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [form, setForm] = useState({
    pipeline_id: '', category_id: '', priority_type_id: '2',
    voter_id: '', leader_id: '', title: '', description: '',
    custom_fields: {} as Record<string, any>, assignee_ids: [] as string[],
  })

  useEffect(() => { fetchData() }, [])
  useEffect(() => { if (selectedPipeline && viewMode === 'board') fetchBoard() }, [selectedPipeline, viewMode])
  useEffect(() => { if (viewMode === 'list') fetchList() }, [viewMode, selectedPipeline, listPage, listPerPage, listSearch, listFilters])

  async function fetchData() {
    setLoading(true)
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(buildApiUrl('/v1/demands/pipelines')),
        axios.get(buildApiUrl('/v1/demands/categories')),
      ])
      setPipelines(pRes.data.data)
      setCategories(cRes.data.data)
      if (pRes.data.data.length > 0 && !selectedPipeline) setSelectedPipeline(pRes.data.data[0].id)
    } catch { /* */ } finally { setLoading(false) }
  }

  async function fetchList() {
    setListLoading(true)
    try {
      const params: any = { page: listPage, per_page: listPerPage }
      if (listFilters.pipeline_id) params.pipeline_id = listFilters.pipeline_id
      else if (selectedPipeline) params.pipeline_id = selectedPipeline
      if (listSearch) params.search = listSearch
      if (listFilters.status) params.status = listFilters.status
      if (listFilters.priority) params.priority = listFilters.priority
      if (listFilters.overdue === '1') params.overdue = '1'
      const res = await axios.get(buildApiUrl('/v1/demands'), { params })
      setListDemands(res.data.data)
      if (res.data.meta) setListMeta(res.data.meta)
    } catch { /* */ } finally { setListLoading(false) }
  }

  async function fetchBoard() {
    if (!selectedPipeline) return
    try {
      const res = await axios.get(buildApiUrl(`/v1/demands/board/${selectedPipeline}`))
      setStages(res.data.data)
    } catch { /* */ }
  }


  function loadLeaders() {
    if (leadersLoaded) return
    axios.get(buildApiUrl('/v1/leaders')).then(r => {
      setLeaders(r.data.data.map((l: any) => ({ id: l.id, name: l.name, nickname: l.nickname })))
      setLeadersLoaded(true)
    }).catch(() => {})
  }

  function openCreate() {
    setForm({ pipeline_id: selectedPipeline ? String(selectedPipeline) : '', category_id: '', priority_type_id: '2', voter_id: '', leader_id: '', title: '', description: '', custom_fields: {}, assignee_ids: user ? [String(user.id)] : [] })
    setErrors({}); setLeaderSearch(''); setSelectedCategoryFields([])
    if (orgUsers.length === 0) {
      axios.get(buildApiUrl('/v1/organization-users')).then(res => setOrgUsers(res.data.data.map((u: any) => ({ id: u.id, name: u.name })))).catch(() => {})
    }
    setCreateModalOpen(true)
  }

  function onCategoryChange(catId: string) {
    const findCat = (cats: Category[]): Category | null => {
      for (const c of cats) { if (c.id === Number(catId)) return c; const f = findCat(c.children || []); if (f) return f }; return null
    }
    const cat = catId ? findCat(categories) : null
    const allFields = cat ? (cat as any).fields || [] : []
    setSelectedCategoryFields(allFields.filter((f: any) => !f.stage || f.stage === 'entry'))
    setForm(f => ({ ...f, category_id: catId, custom_fields: {}, title: cat && !f.title ? cat.name : f.title }))
  }

  function flatCats(cats: Category[], lvl = 0): { id: number; name: string; level: number }[] {
    const r: { id: number; name: string; level: number }[] = []
    for (const c of cats) { r.push({ id: c.id, name: c.name, level: lvl }); if (c.children) r.push(...flatCats(c.children, lvl + 1)) }
    return r
  }

  async function handleCreate() {
    if (saving) return
    const e: Record<string, string> = {}
    if (!form.title.trim()) e.title = 'Título é obrigatório'
    if (!form.pipeline_id) e.pipeline_id = 'Selecione o funil'
    setErrors(e); if (Object.keys(e).length > 0) return

    setSaving(true)
    try {
      await axios.post(buildApiUrl('/v1/demands'), {
        pipeline_id: Number(form.pipeline_id), category_id: form.category_id ? Number(form.category_id) : null,
        priority_type_id: Number(form.priority_type_id), voter_id: form.voter_id ? Number(form.voter_id) : null,
        leader_id: form.leader_id ? Number(form.leader_id) : null, title: form.title, description: form.description || null,
        custom_fields: Object.keys(form.custom_fields).length > 0 ? form.custom_fields : null,
        assignee_ids: form.assignee_ids.map(Number),
      })
      toast('success', 'Demanda criada'); setCreateModalOpen(false); fetchBoard(); fetchData()
    } catch (err: any) { toast('error', err.response?.data?.error || 'Erro ao criar') } finally { setSaving(false) }
  }

  const [movingDemandId, setMovingDemandId] = useState<number | null>(null)

  async function moveDemand(demandId: number, stageId: number, fromStageId: number) {
    if (stageId === fromStageId || movingDemandId) return
    setMovingDemandId(demandId)
    try { await axios.put(buildApiUrl(`/v1/demands/${demandId}/stage`), { stage_id: stageId }); fetchBoard()
    } catch (err: any) { toast('error', err.response?.data?.error || 'Erro ao mover') } finally { setMovingDemandId(null) }
  }

  const pipCats = categories.filter(c => c.pipeline_id === Number(form.pipeline_id))
  const activeFilterCount = [listFilters.status, listFilters.priority, listFilters.pipeline_id, listFilters.overdue].filter(Boolean).length

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Demandas</h1>
          <p className="text-gray-500 mt-1">Gerencie solicitações e demandas</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button onClick={() => setViewMode('board')} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5', viewMode === 'board' ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-500')}>
              <LayoutGrid className="w-3.5 h-3.5" /> Board
            </button>
            <button onClick={() => setViewMode('list')} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5', viewMode === 'list' ? 'bg-white text-gray-800 shadow-xs' : 'text-gray-500')}>
              <List className="w-3.5 h-3.5" /> Lista
            </button>
          </div>
          <Button icon={<Plus className="w-4 h-4" />} onClick={openCreate}>Nova Demanda</Button>
        </div>
      </div>

      {pipelines.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {pipelines.map(p => (
            <button key={p.id} onClick={() => setSelectedPipeline(p.id)}
              className={clsx('flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all',
                selectedPipeline === p.id
                  ? 'bg-white border border-gray-200 shadow-sm text-gray-900'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              )}>
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#6b7280' }} />
              {p.name}
              <span className={clsx('text-xs px-1.5 py-0.5 rounded-full',
                selectedPipeline === p.id ? 'bg-brand-50 text-brand-600' : 'bg-gray-100 text-gray-400'
              )}>{p.demands_count}</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="w-72 flex-shrink-0 bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
              <div className="h-1.5 bg-gray-200" />
              <div className="px-3 py-3 border-b border-gray-100 flex items-center justify-between">
                <div className="h-4 w-24 bg-gray-200 rounded" />
                <div className="h-5 w-8 bg-gray-100 rounded-full" />
              </div>
              <div className="p-3 space-y-2.5">
                {[1,2,3].map(j => (
                  <div key={j} className="bg-gray-50 rounded-xl p-3 space-y-2">
                    <div className="h-4 w-full bg-gray-200 rounded" />
                    <div className="h-3 w-2/3 bg-gray-100 rounded" />
                    <div className="flex justify-between pt-1">
                      <div className="h-3 w-16 bg-gray-100 rounded-full" />
                      <div className="h-3 w-10 bg-gray-100 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : pipelines.length === 0 ? (
        <Card>
          <EmptyState icon={<LayoutGrid className="w-7 h-7" />} title="Nenhum funil configurado"
            description="Configure funis em Configurações > Demandas para começar."
            action={<Button size="sm" onClick={() => navigate('/settings/demands')}>Configurar</Button>} />
        </Card>
      ) : viewMode === 'board' ? (
        <>
          {/* Barra de métricas */}
          {stages.length > 0 && (
            <div className="flex items-center gap-6 px-1 pb-2">
              <div className="text-xs text-gray-500">
                Total: <strong className="text-gray-800">{stages.reduce((s, st) => s + (st.demands?.length || 0), 0)}</strong> demandas
              </div>
              {stages.some(st => (st.demands || []).some(d => d.is_overdue)) && (
                <div className="flex items-center gap-1 text-xs text-error-500">
                  <AlertTriangle className="w-3 h-3" />
                  <strong>{stages.reduce((s, st) => s + (st.demands || []).filter(d => d.is_overdue).length, 0)}</strong> atrasadas
                </div>
              )}
            </div>
          )}

          {/* Board */}
          <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '400px' }}>
            {stages.map(stage => {
              const demandCount = stage.demands?.length || 0
              const overdueCount = (stage.demands || []).filter(d => d.is_overdue).length
              return (
                <div key={stage.id} className="w-72 flex-shrink-0 flex flex-col">
                  <div className="flex-1 bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
                    {/* Barra de cor no topo */}
                    <div className="h-1.5 w-full" style={{ backgroundColor: stage.color }} />

                    {/* Column header */}
                    <div className="px-3 pt-2.5 pb-2 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-800">{stage.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {overdueCount > 0 && <span className="text-[10px] text-error-500 bg-error-50 px-1.5 py-0.5 rounded-full font-medium">{overdueCount}</span>}
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{demandCount}</span>
                        </div>
                      </div>
                    </div>

                    {/* Cards */}
                    <div className="flex-1 space-y-2.5 p-3 min-h-[200px]"
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.backgroundColor = '#eff6ff' }}
                    onDragLeave={e => { e.currentTarget.style.backgroundColor = '' }}
                    onDrop={e => { e.currentTarget.style.backgroundColor = ''; const did = Number(e.dataTransfer.getData('demandId')); const fromStage = Number(e.dataTransfer.getData('fromStageId')); if (did) moveDemand(did, stage.id, fromStage) }}>
                    {(stage.demands || []).map(d => (
                      <div key={d.id} draggable onDragStart={e => { e.dataTransfer.setData('demandId', String(d.id)); e.dataTransfer.setData('fromStageId', String(stage.id)) }}
                        onClick={() => navigate(`/demands/${d.id}`)}
                        className="bg-gray-50 rounded-xl cursor-grab active:cursor-grabbing hover:shadow-sm hover:bg-white transition-all overflow-hidden border border-gray-100"
                        >

                        <div className="p-3">
                          <p className="text-[13px] font-medium text-gray-900 line-clamp-2 leading-snug">{d.title}</p>

                          {d.voter_name && (
                            <p className="text-xs text-gray-500 mt-1.5 truncate">{d.voter_name}</p>
                          )}

                          <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-50">
                            <div className="flex items-center gap-1.5">
                              {d.category_name && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{d.category_name}</span>}
                            </div>
                            {d.is_overdue ? (
                              <span className="flex items-center gap-1 text-[10px] text-error-500 font-semibold bg-error-50 px-1.5 py-0.5 rounded-full"><AlertTriangle className="w-3 h-3" /> {d.days_in_stage}d</span>
                            ) : d.days_in_stage != null ? (
                              <span className="flex items-center gap-1 text-[10px] text-gray-400"><Clock className="w-3 h-3" /> {d.days_in_stage}d</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}

                    {demandCount === 0 && (
                      <div className="flex items-center justify-center h-20 text-xs text-gray-400">Nenhuma demanda</div>
                    )}
                  </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <SearchInput placeholder="Buscar demanda ou solicitante..." value={listSearch} onChange={e => { setListSearch(e.target.value); setListPage(1) }} />
            </div>
            <button
              onClick={() => { setTempFilters(listFilters); setFilterPanelOpen(true) }}
              className={clsx('h-11 px-4 flex items-center gap-2 rounded-lg border text-sm font-medium transition-colors',
                activeFilterCount > 0 ? 'bg-brand-50 border-brand-200 text-brand-600' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              )}>
              <SlidersHorizontal className="w-4 h-4" /> Filtros
              {activeFilterCount > 0 && <span className="ml-1 bg-brand-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>}
            </button>
          </div>

          {listLoading ? (
            <Card padding="none">
              <div className="p-5 space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}</div>
            </Card>
          ) : listDemands.length === 0 ? (
            <Card>
              <EmptyState icon={<List className="w-7 h-7" />} title="Nenhuma demanda encontrada" description={listSearch ? 'Tente outra busca.' : 'Crie a primeira demanda.'} />
            </Card>
          ) : (
            <Card padding="none">
              {/* Resumo */}
              <div className="flex items-center gap-6 px-5 py-3 border-b border-gray-100">
                <span className="text-xs text-gray-500">{listMeta.total} demanda{listMeta.total !== 1 ? 's' : ''}</span>
              </div>

              {/* Lista */}
              <div className="divide-y divide-gray-50">
                {listDemands.map(d => (
                  <div key={d.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => navigate(`/demands/${d.id}`)}>
                    {/* Prioridade */}
                    <div className="w-1 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: d.is_overdue ? '#ef4444' : d.priority_color }} />

                    {/* Info principal */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-gray-900 truncate">{d.title}</p>
                        {d.is_overdue && <span className="flex items-center gap-0.5 text-[10px] text-error-500 font-semibold bg-error-50 px-1.5 py-0.5 rounded-full flex-shrink-0"><AlertTriangle className="w-2.5 h-2.5" /> Atrasada</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {d.voter_name && <span className="font-medium text-gray-700">{d.voter_name}</span>}
                        {d.category_name && <span className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{d.category_name}</span>}
                        <span className="text-gray-400">{d.pipeline_name}</span>
                      </div>
                    </div>

                    {/* Etapa */}
                    <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 w-28">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.stage_color }} />
                      <span className="text-xs text-gray-600 truncate">{d.stage_name}</span>
                    </div>

                    {/* Prioridade badge */}
                    <div className="hidden md:block flex-shrink-0 w-20">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg" style={{ backgroundColor: d.priority_color + '15', color: d.priority_color }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: d.priority_color }} />
                        {d.priority_label}
                      </span>
                    </div>

                    {/* Tempo */}
                    <div className="hidden lg:flex items-center justify-center flex-shrink-0 w-14">
                      {d.days_in_stage != null ? (
                        <span className={clsx('flex items-center gap-1 text-xs', d.is_overdue ? 'text-error-500 font-medium' : 'text-gray-400')}>
                          <Clock className="w-3 h-3" /> {d.days_in_stage}d
                        </span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </div>

                    {/* Status */}
                    <div className="flex-shrink-0">
                      <Badge variant={d.status_code === 'OPEN' ? 'brand' : d.status_code === 'COMPLETED' ? 'success' : 'gray'} size="sm" dot>{d.status_label}</Badge>
                    </div>

                    {/* Data */}
                    <div className="hidden xl:block flex-shrink-0 w-20 text-right">
                      <span className="text-xs text-gray-400">{formatRelativeDate(d.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <PaginationBar page={listPage} perPage={listPerPage} total={listMeta.total} lastPage={listMeta.last_page} onPageChange={setListPage} onPerPageChange={n => { setListPerPage(n); setListPage(1) }} />
            </Card>
          )}
        </div>
      )}

      {/* Painel filtros */}
      <SlidePanel open={filterPanelOpen} onClose={() => setFilterPanelOpen(false)} title="Filtros" size="sm">
        <div className="p-6 space-y-4">
          <Select label="Funil" value={tempFilters.pipeline_id}
            onChange={e => setTempFilters({ ...tempFilters, pipeline_id: e.target.value })}
            options={pipelines.map(p => ({ value: String(p.id), label: p.name }))} placeholder="Todos" />

          <Select label="Status" value={tempFilters.status}
            onChange={e => setTempFilters({ ...tempFilters, status: e.target.value })}
            options={[{ value: 'OPEN', label: 'Em andamento' }, { value: 'COMPLETED', label: 'Concluída' }, { value: 'CANCELLED', label: 'Cancelada' }]}
            placeholder="Todos" />

          <Select label="Prioridade" value={tempFilters.priority}
            onChange={e => setTempFilters({ ...tempFilters, priority: e.target.value })}
            options={[{ value: 'LOW', label: 'Baixa' }, { value: 'NORMAL', label: 'Normal' }, { value: 'HIGH', label: 'Alta' }, { value: 'URGENT', label: 'Urgente' }]}
            placeholder="Todas" />

          <Select label="Atrasadas" value={tempFilters.overdue}
            onChange={e => setTempFilters({ ...tempFilters, overdue: e.target.value })}
            options={[{ value: '1', label: 'Apenas atrasadas' }]}
            placeholder="Todas" />

          <div className="pt-4 flex gap-3">
            <Button onClick={() => { setListFilters(tempFilters); setListPage(1); setFilterPanelOpen(false) }} className="flex-1">Aplicar filtros</Button>
            <Button variant="outline" onClick={() => { const empty = { status: '', priority: '', pipeline_id: '', overdue: '' }; setTempFilters(empty); setListFilters(empty); setListPage(1); setFilterPanelOpen(false) }} className="flex-1">Limpar</Button>
          </div>
        </div>
      </SlidePanel>

      {/* Modal criar */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)} size="lg">
        <ModalHeader onClose={() => setCreateModalOpen(false)}>Nova Demanda</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Select label="Funil" value={form.pipeline_id} onChange={e => { setForm(f => ({ ...f, pipeline_id: e.target.value, category_id: '' })); setSelectedCategoryFields([]) }}
                options={pipelines.map(p => ({ value: String(p.id), label: p.name }))} placeholder="Selecione" error={errors.pipeline_id} />
              <Select label="Prioridade" value={form.priority_type_id} onChange={e => setForm(f => ({ ...f, priority_type_id: e.target.value }))}
                options={[{ value: '1', label: 'Baixa' }, { value: '2', label: 'Normal' }, { value: '3', label: 'Alta' }, { value: '4', label: 'Urgente' }]} placeholder="Selecione" />
            </div>

            {form.pipeline_id && pipCats.length > 0 && (
              <Select label="Categoria" value={form.category_id} onChange={e => onCategoryChange(e.target.value)}
                options={flatCats(pipCats).map(c => ({ value: String(c.id), label: c.level > 0 ? '  '.repeat(c.level) + c.name : c.name }))} placeholder="Selecione" />
            )}

            {selectedCategoryFields.length > 0 && (
              <div className="space-y-3">
                {selectedCategoryFields.map((field: any) => (
                  <div key={field.key}>
                    {field.type === 'text' && <Input label={field.label} value={form.custom_fields[field.key] || ''} onChange={e => setForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.value } }))} />}
                    {field.type === 'number' && <Input label={field.label} type="number" value={form.custom_fields[field.key] || ''} onChange={e => setForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.value } }))} />}
                    {field.type === 'date' && <Input label={field.label} type="date" value={form.custom_fields[field.key] || ''} onChange={e => setForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.value } }))} />}
                    {field.type === 'textarea' && <Textarea label={field.label} value={form.custom_fields[field.key] || ''} onChange={e => setForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.value } }))} rows={2} />}
                    {field.type === 'select' && <Select label={field.label} value={form.custom_fields[field.key] || ''} onChange={e => setForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.value } }))} options={(field.options || []).map((o: string) => ({ value: o, label: o }))} placeholder="Selecione" />}
                    {field.type === 'boolean' && (
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={!!form.custom_fields[field.key]} onChange={e => setForm(f => ({ ...f, custom_fields: { ...f.custom_fields, [field.key]: e.target.checked } }))} className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                        <span className="text-sm text-gray-700">{field.label}</span>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            )}

            <Input label="Título" value={form.title} onChange={e => { setForm(f => ({ ...f, title: e.target.value })); setErrors(er => ({ ...er, title: '' })) }}
              placeholder="Resumo da demanda" error={errors.title} />

            <Textarea label="Observações" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Detalhes adicionais..." rows={2} />

            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Solicitante</p>
              <div className="space-y-3">
                <VoterSearchSelect
                  label="Pessoa"
                  value={form.voter_id}
                  onChange={(id) => setForm(f => ({ ...f, voter_id: id }))}
                  createType="voter"
                />
                {/* Liderança */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Liderança</label>
                  <div className="relative">
                    <input value={form.leader_id ? leaders.find(l => l.id === Number(form.leader_id))?.name || leaderSearch : leaderSearch}
                      onChange={e => { setLeaderSearch(e.target.value); setForm(f => ({ ...f, leader_id: '' })); if (!leaderDropdownOpen) setLeaderDropdownOpen(true) }}
                      onFocus={() => { loadLeaders(); setLeaderDropdownOpen(true) }} placeholder="Buscar liderança..." className={inputClass} />
                    {form.leader_id && <button onClick={() => { setForm(f => ({ ...f, leader_id: '' })); setLeaderSearch('') }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>}
                    {leaderDropdownOpen && !form.leader_id && (<>
                      <div className="fixed inset-0" style={{ zIndex: 100002 }} onClick={() => setLeaderDropdownOpen(false)} />
                      <div className="absolute left-0 right-0 top-12 bg-white rounded-xl shadow-lg border border-gray-200 max-h-48 overflow-y-auto" style={{ zIndex: 100003 }}>
                        {leaders.filter(l => !leaderSearch || l.name.toLowerCase().includes(leaderSearch.toLowerCase())).map(l => (
                          <button key={l.id} onClick={() => { setForm(f => ({ ...f, leader_id: String(l.id) })); setLeaderSearch(''); setLeaderDropdownOpen(false) }}
                            className="w-full text-left px-4 py-2.5 hover:bg-gray-50"><span className="text-sm text-gray-900">{l.name}</span>{l.nickname && <span className="text-xs text-gray-400 ml-2">({l.nickname})</span>}</button>
                        ))}
                      </div>
                    </>)}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <MultiSelect
                label="Responsáveis"
                selected={form.assignee_ids}
                onChange={ids => setForm(f => ({ ...f, assignee_ids: ids }))}
                options={orgUsers.map(u => ({ value: String(u.id), label: u.name }))}
                placeholder="Selecione os responsáveis"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreate} loading={saving}>Criar demanda</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
